import { inject, injectable } from "tsyringe";
import RunningActionStore, { TOKEN_RunningActionStore } from "./utility-stores/RunningActionStore";
import { makeAutoObservable } from "mobx";
import { ModalDialogStore, TOKEN_ModalDialogStore } from "./modalDialogs/ModalDialogStore";
import { BlockingMessageType } from "@/components/BlockingMessage";
import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import { TOKEN_INavigationRedirect, type INavigationRedirect } from "@/services/NavigationRedirect";
import { ProjectBusEvents, ServerIssueType, TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";

export const TOKEN_LoadingStore = Symbol("LoadingStore");

@injectable()
export class LoadingStore {
    private clearBlockingMessage() {
        this._blockingMessage = undefined;
        this._blockingMessageType = undefined;
        this._continueMessage = undefined;
    }
    private _blockingMessage: string | undefined;
    private _continueAction: (() => Promise<void> | void) | undefined;
    private _continueMessage: string | undefined;
    private _blockingMessageType: BlockingMessageType | undefined;

    get blockingMessage(): string | undefined { return this._blockingMessage; }

    get continueMessage(): string | undefined { return this._continueMessage; }

    get blockingMessageType(): BlockingMessageType | undefined { return this._blockingMessageType; }

    get continueAction() { return this._continueAction; }

    showBlockingMessage(type: BlockingMessageType, message?: string, continueMessage?: string, continueAction?: () => Promise<void> | void) {
        this._blockingMessageType = type;
        this._blockingMessage = message;
        this._continueMessage = continueMessage;
        this._continueAction = continueAction;
    }

    show404 = () => {
        this.showBlockingMessage(BlockingMessageType.NotFound, this.t.t('cant_find_project'), this.t.t("create_new_project_instead"), async () => {
            await this.navigationRedirect.createNewProject(64);
            this.clearBlockingMessage();
        });
    }

    show403 = () => {
        this.showBlockingMessage(BlockingMessageType.NoPermission, this.t.t('no_permission_project'), this.t.t("create_new_project_instead"), async () => {
            await this.navigationRedirect.createNewProject(64);
            this.clearBlockingMessage();
        });
    }

    showPendingDecryption = () => {
        this.showBlockingMessage(BlockingMessageType.NoPermission, this.t.t('pending_key'), this.t.t("create_new_project_instead"), async () => {
            await this.navigationRedirect.createNewProject(64);
            this.clearBlockingMessage();
        });
    }

    show500 = () => {
        this.showBlockingMessage(BlockingMessageType.ServerError, this.t.t('server_error'), this.t.t("create_new_project_instead"), async () => {
            await this.navigationRedirect.createNewProject(64);
            this.clearBlockingMessage();
        });
    }

    constructor(
        @inject(TOKEN_RunningActionStore) private readonly runningActionStore: RunningActionStore,
        @inject(TOKEN_ModalDialogStore) private readonly modalDialogStore: ModalDialogStore,
        @inject(TOKEN_IWrapTranslations) private readonly t: IWrapTranslations,
        @inject(TOKEN_INavigationRedirect) private readonly navigationRedirect: INavigationRedirect,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
    ) {
        makeAutoObservable(this);

        projectBus.subscribe_serverIssue(async (data: ProjectBusEvents['SERVER_ISSUE']) => {
            switch (data.serverIssue) {
                case ServerIssueType.NotFound: this.show404(); break;
                case ServerIssueType.NoPermission: this.show403(); break;
                case ServerIssueType.ServerError: this.show500(); break;
                case ServerIssueType.PendingDecryption: this.showPendingDecryption(); break;
                case ServerIssueType.Resolved: this.clearBlockingMessage(); break;
            }
        })
    }

    /**
     * Returns the message to display in the loading screen.
     */
    public get message(): string {
        return this.runningActionStore.message;
    }

    /**
     * Returns true if a blocking action is running and no modal dialog is open.
     */
    public get showBlockingLoadingScreen(): boolean {
        return this.runningActionStore.isRunningBlockingAction && !this.modalDialogStore.isOpen;
    }
}